/**
 * The one place a version is written by path, for both product modes.
 *
 * PLAYENGINE writes through the document store: only the nodes on the path are
 * copied and everything off it is shared, so a keystroke costs the depth of the
 * path rather than the weight of the playable. Untouched branches keep their
 * identity, which is what lets a memoised child skip a render and what the
 * per-path save in versionPlayEngine/updateOne is built on.
 *
 * NORMAL PLAYABLES KEEP THE DEEP CLONE, and this is not an oversight. Their
 * panels edit config subtrees in place — containerVersionSettings deletes keys
 * straight out of `props.selectedVersion.gameConfig.components.enemy1` and
 * assigns into its `selectedImages` — so with structure shared between versions
 * those mutations would reach objects that are meant to be older snapshots, the
 * undo stack's `gameConfig` among them. Sharing is only safe where nothing
 * mutates, and that is the playEngine side.
 */

import _ from "lodash";
import { perfStart } from "./playEnginePerf";
import { syncDoc, writePath } from "./playEngineDocStore";

/** The document store addresses an open version by its id. */
export function docKeyFor(version) {
  return String(_.get(version, "_id", ""));
}

/**
 * Apply path writes to a version, returning a new version object. The input is
 * never mutated in either mode.
 *
 * @param {object} baseVersion
 * @param {Array<[string, *]>} entries [path, value] pairs, applied in order
 * @param {{ useDocStore?: boolean }} [options] true for playEngine bases
 * @returns {object} the new version
 */
export function applyVersionPaths(baseVersion, entries, { useDocStore = false } = {}) {
  if (!useDocStore) {
    const endClone = perfStart("persist:cloneDeep(version)");
    const next = _.cloneDeep(baseVersion);
    endClone();
    entries.forEach(([path, value]) => _.set(next, path, value));
    return next;
  }

  const endWrite = perfStart("persist:setIn(version)");
  const key = docKeyFor(baseVersion);
  syncDoc(key, baseVersion);
  let next = baseVersion;
  entries.forEach(([path, value]) => {
    next = writePath(key, path, value);
  });
  endWrite();
  return next;
}
